/**
 * Isolation PAR ONGLET de la session d'impersonation Super Admin.
 *
 * Problème résolu (bug 11/06/2026) : la fenêtre d'impersonation écrivait le token
 * impersonné dans le localStorage PARTAGÉ entre tous les onglets → l'onglet Super
 * Admin se retrouvait avec le token du client (403 partout, déconnexion en cascade
 * via l'intercepteur 401 qui purgeait le token, cassant à son tour la fenêtre
 * d'impersonation : « les biens ne chargent pas »).
 *
 * Solution : dans un onglet marqué "impersonation" (flag en sessionStorage, donc
 * PROPRE à l'onglet), les clés d'auth sont redirigées vers sessionStorage.
 * Les 70+ call-sites `localStorage.getItem('token')` restent inchangés : on
 * remplace `window.localStorage` par une façade qui route les clés isolées.
 *
 * ⚠️ Ce module doit être importé EN PREMIER dans index.js (avant App).
 */

const IMP_FLAG = '__komerces_imp_tab__';
// Clés propres à la session de l'onglet impersonné (auth + cache branding)
const ISOLATED_KEYS = ['token', 'user', 'impersonating', 'agence_branding'];
const ISOLATED = new Set(ISOLATED_KEYS);

const realLocal = window.localStorage;

// fetch natif capturé AVANT l'override d'App.js (qui écrase l'en-tête Authorization
// avec le token de l'onglet — inutilisable pour renouveler avec le token Super Admin).
export const nativeFetch = window.fetch.bind(window);

/** Lit une clé dans le VRAI localStorage partagé (ignore l'isolation de l'onglet). */
export function getRealLocalStorageItem(key) {
  return realLocal.getItem(key);
}

const isImpTab = () => {
  try {
    return window.sessionStorage.getItem(IMP_FLAG) === '1';
  } catch (_) {
    return false;
  }
};

const facade = {
  getItem(key) {
    if (isImpTab() && ISOLATED.has(key)) return window.sessionStorage.getItem(key);
    return realLocal.getItem(key);
  },
  setItem(key, value) {
    if (isImpTab() && ISOLATED.has(key)) {
      window.sessionStorage.setItem(key, value);
      return;
    }
    realLocal.setItem(key, value);
  },
  removeItem(key) {
    if (isImpTab() && ISOLATED.has(key)) {
      window.sessionStorage.removeItem(key);
      return;
    }
    realLocal.removeItem(key);
  },
  clear() {
    if (isImpTab()) {
      try {
        ISOLATED_KEYS.concat(IMP_FLAG).forEach((k) => window.sessionStorage.removeItem(k));
      } catch (_) { /* noop */ }
      return; // un clear() dans l'onglet impersonné ne doit PAS déconnecter les autres onglets
    }
    realLocal.clear();
  },
  key(i) {
    return realLocal.key(i);
  },
  get length() {
    return realLocal.length;
  },
};

/** À appeler dans l'onglet enfant à réception des données d'impersonation. */
export function activateImpersonationTab({ token, user }) {
  const ss = window.sessionStorage;
  ss.setItem(IMP_FLAG, '1');
  ss.setItem('token', token);
  ss.setItem('user', typeof user === 'string' ? user : JSON.stringify(user || {}));
  ss.setItem('impersonating', 'true');
}

/** Sort l'onglet du mode impersonation (le localStorage réel redevient visible). */
export function deactivateImpersonationTab() {
  const ss = window.sessionStorage;
  ISOLATED_KEYS.concat(IMP_FLAG).forEach((k) => {
    try { ss.removeItem(k); } catch (_) { /* noop */ }
  });
}

try {
  Object.defineProperty(window, 'localStorage', {
    configurable: true,
    get: () => facade,
  });
} catch (e) {
  // Navigateur récalcitrant : retour au comportement historique (storage partagé).
  // eslint-disable-next-line no-console
  console.warn('[impersonation] Isolation par onglet indisponible :', e);
}

// ── Auto-réparation au boot ───────────────────────────────────────────────
// 1. Purge des clés héritées de l'ANCIENNE implémentation d'impersonation
//    (token/user impersonnés écrits dans le localStorage partagé).
// 2. Purge des valeurs empoisonnées ("undefined"/"null") qui font planter
//    JSON.parse au rendu → page blanche.
try {
  ['superAdminToken', 'superAdminUser'].forEach((k) => realLocal.removeItem(k));
  if (!isImpTab()) realLocal.removeItem('impersonating');
  ['token', 'user'].forEach((k) => {
    const v = realLocal.getItem(k);
    if (v === 'undefined' || v === 'null' || v === '') realLocal.removeItem(k);
  });
  const rawUser = realLocal.getItem('user');
  if (rawUser) {
    try {
      JSON.parse(rawUser);
    } catch (_) {
      realLocal.removeItem('user');
      realLocal.removeItem('token'); // session incohérente → repartir proprement
    }
  }
} catch (_) { /* noop */ }
